// Lead Drop Popup Variants Configuration

import { setLeadDropPopup1 } from "@/store/slices/university";
import type { UniversityState } from "@/store/slices/university";
import { Form_4, SegmentWorkbenchForm } from "@/components/organisms/admin/forms";
import { LEAD_DROP_POPUP_VARIANTS } from "../../sectionTypes";
import { LEAD_DROP_POPUP_1_CONSTANTS } from "../../fields/leadDropPopup/leadDropPopup_1";
import { LEAD_DROP_POPUP_2_CONSTANTS } from "../../fields/leadDropPopup/leadDropPopup_2";
import { LEAD_DROP_POPUP_3_CONSTANTS } from "../../fields/leadDropPopup/leadDropPopup_3";
import { LEAD_DROP_POPUP_4_CONSTANTS } from "../../fields/leadDropPopup/leadDropPopup_4";
import { LEAD_DROP_POPUP_7_CONSTANTS } from "../../fields/leadDropPopup/leadDropPopup_7";
import { LEAD_DROP_POPUP_10_CONSTANTS } from "../../fields/leadDropPopup/leadDropPopup_10";
import { LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS } from "../../fields/leadDropForm/leadDropForm_segmented";
import {
  LEAD_DROP_POPUP_8_CONSTANTS,
  buildLeadDrop8DynamicDefinitions,
} from "../../fields/leadDropPopup/leadDropPopup_8";
import { LEAD_DROP_POPUP_9_CONSTANTS } from "../../fields/leadDropPopup/leadDropPopup_9";
import { LEAD_DROP_POPUP_5002_CONSTANTS } from "../../fields/leadDropPopup/leadDropPopup_5002";
import { LEAD_DROP_POPUP_5018_CONSTANTS } from "../../fields/leadDropPopup/leadDropPopup_5018";
import { LEAD_DROP_POPUP_2001_CONSTANTS } from "../../fields/leadDropPopup/leadDropPopup_2001";
import { updateLeadDropPopup } from "@/api/admin/leadDropPopup";
import type { VariantConfig } from "../types";
import { COMMON_CONSTANTS } from "../types";

/**
 * Variant 8 has no `dynamic_field_definitions` JSON field in the admin UI; the
 * explicit Name/Email/Qualification/Program label/placeholder/required keys (stored
 * in `additional_fields`) are recompiled into that JSON on every save so the
 * unchanged Leaddrop8 component can read it.
 */
const withCompiledLeadDrop8Dynamic = (payload: any) => {
  // Strip any top-level `dynamic_field_definitions` (it is not a DB column; it
  // lives inside additional_fields and is recompiled below).
  const { dynamic_field_definitions: _leaked, ...rest } = payload || {};
  const additional =
    rest && typeof rest.additional_fields === "object" && rest.additional_fields
      ? rest.additional_fields
      : {};
  return {
    ...rest,
    additional_fields: {
      ...additional,
      dynamic_field_definitions: buildLeadDrop8DynamicDefinitions(additional),
    },
  };
};

export const LEAD_DROP_POPUP_VARIANTS_CONFIG: Record<string, VariantConfig> = {
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_1]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_1,
    name: "Lead Drop Popup 1",
    description: "Lead Drop Popup 1",
    componentFields: LEAD_DROP_POPUP_1_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_1_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_2]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_2,
    name: "Lead Drop Popup 2",
    description: "Lead Drop Popup 2",
    componentFields: LEAD_DROP_POPUP_2_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_2_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  // ── Net-new opt-in variants (ported from the multi-tenant line) ─────────────
  // Variant 3 — "Drop a query" popup. On submit it writes the lead straight to the
  // CMS database via POST /leads/direct (createLeadDirect): NO OTP and NO ERP/LSQ
  // push. Use it for lightweight enquiry capture. Works identically in standalone
  // and multi-tenant.
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_3]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_3,
    name: "Lead Drop Popup 3",
    description:
      "Drop a query (name, email, phone, details). Saves to the CMS database only (no OTP, no ERP/LSQ) via /leads/direct.",
    componentFields: LEAD_DROP_POPUP_3_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_3_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  // Variant 4 — same single-step form as popup 1 but NO OTP. On submit the saved
  // lead is pushed to CRM via POST /leads/sync-drop4. Standalone always routes to
  // ERP; LSQ is used only on multi-tenant microsite hosts (isMultiTenant gate in
  // services/leadDrop4.js). The CMS destination field is honoured only in
  // multi-tenant mode.
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_4]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_4,
    name: "Lead Drop Popup 4",
    description:
      "Same single-step form as popup 1 but no OTP; on submit pushes the lead to CRM (/leads/sync-drop4). Standalone → ERP; LSQ only for multi-tenant microsites.",
    componentFields: LEAD_DROP_POPUP_4_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_4_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_7]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_7,
    name: "Lead Drop Popup 7",
    description:
      "IIT-style phone-first lead popup with JSON-configurable secondary fields.",
    componentFields: LEAD_DROP_POPUP_7_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_7_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_10]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_10,
    name: "Lead Drop Popup 10",
    description:
      "Single-step lead popup that collects full name, phone, email, highest qualification and area of interest with OTP verification.",
    componentFields: LEAD_DROP_POPUP_10_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_10_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_6]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_6,
    name: "Multi-step Lead Drop Form",
    description: "Same tabbed configuration as lead drop form.",
    componentFields: LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS.MERGED_FIELDS_CONFIG,
    segmentDefinitions: [...LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS.SEGMENT_WORKBENCH_SEGMENTS],
    globalSegmentFields: [...LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS.LEAD_STRIP_GLOBAL_FIELDS],
    metadataFields: [],
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: SegmentWorkbenchForm,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_8]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_8,
    name: "Lead Drop Popup 8",
    description:
      "IIT-style phone-first lead popup. Secondary fields (Name / Email / Qualification / Program) configured via explicit label & placeholder.",
    componentFields: LEAD_DROP_POPUP_8_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_8_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: (id: number, data: any) =>
      updateLeadDropPopup(id, withCompiledLeadDrop8Dynamic(data)),
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_9]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_9,
    name: "Lead Drop Popup 9",
    description:
      "Apply Now flow — single-step form with an optional countdown, OTP, and Thank You / Welcome Back / Downloaded last cards (configurable circular background).",
    componentFields: LEAD_DROP_POPUP_9_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_9_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_2001]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_2001,
    name: "Lead Drop Popup 2001",
    description:
      "Single-step popup — name, email, phone, qualification and area of interest on one screen, with the OTP revealed inline as soon as the phone validates. A course marked Certification Programme continues to a second step (background details) and LMS signup; every other course finishes on the thank-you / welcome-back / brochure card.",
    componentFields: LEAD_DROP_POPUP_2001_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_2001_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_5002]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_5002,
    name: "Lead Drop Popup 5002",
    description:
      "BDU-style single-step popup — name, phone, email, course and qualification on one screen with the OTP revealed inline (no second step), then Registered / Welcome Back / Download last cards.",
    componentFields: LEAD_DROP_POPUP_5002_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_5002_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
  [LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_5018]: {
    id: LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_5018,
    name: "Lead Drop Popup 5018",
    description:
      "KUK-style phone-first popup — the phone number alone, then name, email, qualification and area of interest revealed for a new lead, then a separate OTP screen with an Edit link and resend countdown, then Registered / Welcome back / Download last cards.",
    componentFields: LEAD_DROP_POPUP_5018_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG,
    metadataFields: LEAD_DROP_POPUP_5018_CONSTANTS.METADATA_FIELDS,
    showTable: false,
    setAction: setLeadDropPopup1,
    formComponent: Form_4,
    previewComponent: null,
    selector: (state: UniversityState) => state.lead_drop_popup_1,
    updateMethod: updateLeadDropPopup,
    transformDataForPreview: (data: any) => ({ data: data || {} }),
    constants: { ...COMMON_CONSTANTS, SUCCESS_MESSAGE: "Lead drop popup saved successfully" },
  },
};

export const getLeadDropPopupVariantDetails = (variantId: string) =>
  LEAD_DROP_POPUP_VARIANTS_CONFIG[variantId];

